/**
 * Reiches, verlustarmes Dokumentmodell — vom Server aus dem OOXML (docx) erzeugt und
 * der KI als strukturierter Snapshot geliefert; die KI bearbeitet/erzeugt denselben
 * Aufbau (eigenes Format, NICHT an Fremdformaten orientiert).
 *
 * Anker bleibt die `paragraph_id` (= Word `uniqueLocalId`); Whitespace wird literal
 * im Text erhalten, Struktur/Marks explizit ausgezeichnet.
 */
import type { ParagraphId } from "./document-status";

export type DocAlignment = "left" | "center" | "right" | "justify";

/** Listen-Info eines Absatzes (Word numbering). */
export interface DocListInfo {
  id?: string; // listId (für Fortsetzung)
  level: number; // 0-basiert
  ordered: boolean; // true = nummeriert, false = Aufzählung
  marker?: string; // gerenderter Marker, falls bekannt (z. B. "1.", "•")
  numStyle?: string; // "arabic" | "upperRoman" | "lowerRoman" | "upperLetter" | "lowerLetter" | "bullet" | ...
  /**
   * Gerenderte Nummer dieses Listenabsatzes in vollem Kontext (z. B. "5.1"), algorithmisch aus der
   * Numbering-Definition berechnet. Damit identifiziert die KI ein Verweisziel deterministisch
   * ("der Absatz mit number 5.1"), statt die Nummer aus dem Text zu erraten. Nur bei nummerierten
   * Listen gesetzt (nicht bei Bullets).
   */
  number?: string;
  /**
   * Eindeutiger Vollkontext-Pfad über alle Ebenen (z. B. "5.1" bzw. "2.2.a"), auch wenn die Anzeige
   * (`number`) nur "(a)" ist. Damit identifiziert die KI einen verschachtelten Unterpunkt dokumentweit
   * eindeutig — ein "(a)" allein ist mehrdeutig.
   */
  numberFull?: string;
  /**
   * Nummerierungs-Anomalie: dieser Unterpunkt zeigt einen numerischen Präfix (z. B. "4.1"), der NICHT
   * zur Nummer des umschließenden Abschnitts passt (`sectionNumber`, z. B. "5") — er liegt auf einer
   * EIGENEN Liste (andere numId) und läuft bei Umnummerierung NICHT mit. Vom Parser deterministisch
   * erkannt, damit die KI das melden kann, statt es selbst herzuleiten.
   */
  continuityBreak?: boolean;
  sectionNumber?: string; // Nummer des umschließenden Abschnitts (nur bei continuityBreak gesetzt)
}

/** Ein Inline-Lauf: Text mit Marks, ODER ein Spezial-Element (Umbruch/Tab/Feld). */
export interface Run {
  t?: string; // Text (literal, inkl. Mehrfach-Leerzeichen)
  b?: true; // bold
  i?: true; // italic
  u?: string; // underline: "single" | "double" | ... (Word-Typ)
  strike?: true;
  sup?: true; // superscript
  sub?: true; // subscript
  caps?: true; // ALL CAPS
  smallCaps?: true;
  color?: string; // Schriftfarbe "RRGGBB"
  bg?: string; // Highlight/Schattierung
  font?: string; // Schriftart-Name
  size?: number; // pt
  link?: string; // Hyperlink-Ziel (URL)
  br?: "line" | "page" | "column"; // expliziter Umbruch (ohne t)
  tab?: true; // Tabulator
  field?: { kind: string; text?: string; code?: string }; // Feld (z. B. REF, PAGE) als Text + Meta + Feldcode
  ins?: { author?: string }; // Tracked Change: Einfügung
  del?: { author?: string }; // Tracked Change: Löschung
}

/** Absatz oder Überschrift. Listen werden über `list` ausgewiesen (keine eigene Block-Art). */
export interface DocParagraph {
  type: "paragraph" | "heading";
  id: ParagraphId;
  level?: number; // nur heading: 1-6
  style?: string; // Style-Name (z. B. "Heading 1", "Normal", custom)
  align?: DocAlignment;
  list?: DocListInfo;
  indent?: { left?: number; right?: number; firstLine?: number };
  spacing?: { before?: number; after?: number; line?: number };
  /**
   * Formatierung der ABSATZMARKE (Pilcrow) — in Word erbt das Aufzählungszeichen / die
   * Nummer diese Run-Properties. Eine rote Farbe hier = rotes Bullet, auch wenn der Text
   * default-farbig ist. Nur Mark-Felder relevant (color/b/i/u/strike/size/font); kein Text.
   */
  marker?: Run;
  /**
   * Namen der Bookmarks, die in diesem Absatz beginnen (z. B. `_Ref498340090`) — die Verweis-ZIELE.
   * Zusammen mit `runs[].field.code` (REF-QUELLEN) ergibt sich der vollständige Querverweis-Graph:
   * Ein REF-Feld mit Code `REF _Ref498340090 …` zeigt auf den Absatz, der dieses Bookmark trägt.
   * So referenziert die KI vorhandene Ziele deterministisch, statt neue Bookmarks zu erfinden.
   */
  bookmarks?: string[];
  runs: Run[];
}

export interface DocTableCell {
  id?: string;
  blocks: DocBlock[]; // Absätze/Listen/Tabellen (Word erlaubt verschachtelte Tabellen)
}
export interface DocTableRow {
  cells: DocTableCell[];
}
export interface DocTable {
  type: "table";
  id?: string;
  rows: DocTableRow[];
}

export type DocBlock = DocParagraph | DocTable;

/** Sichtbarer Text einer Run-Folge: Text-Läufe + Tabs/Umbrüche/Feld-Text (Marks ignoriert). */
export function runsToText(runs: Run[] | undefined): string {
  if (!runs) return "";
  let out = "";
  for (const r of runs) {
    if (r.t) out += r.t;
    else if (r.tab) out += "\t";
    else if (r.br) out += "\n";
    else if (r.field?.text) out += r.field.text;
  }
  return out;
}

/** Gerenderte Länge eines Laufs im Text von runsToText (Text-Länge bzw. 1 für tab/br, Feld-Textlänge). */
function runLength(r: Run): number {
  if (r.t) return r.t.length;
  if (r.tab) return 1;
  if (r.br) return 1;
  if (r.field?.text) return r.field.text.length;
  return 0;
}

/**
 * Schneidet eine Lauf-Folge auf das Zeichen-Intervall [start,end) von runsToText zu und
 * ERHÄLT die Marks. Text-Läufe werden gesplittet; tab/br/field sind atomar und werden ganz
 * aufgenommen, sobald sie das Intervall überlappen. Für formatierte Teil-Einfügungen (Diff).
 */
export function sliceRuns(runs: Run[], start: number, end: number): Run[] {
  const out: Run[] = [];
  let pos = 0;
  for (const r of runs) {
    const len = runLength(r);
    if (len === 0) continue;
    const rStart = pos;
    const rEnd = pos + len;
    pos = rEnd;
    if (rEnd <= start || rStart >= end) continue; // keine Überlappung
    if (r.t) {
      const from = Math.max(0, start - rStart);
      const to = Math.min(len, end - rStart);
      const sliced = r.t.slice(from, to);
      if (sliced) out.push({ ...r, t: sliced });
    } else {
      out.push({ ...r }); // tab/br/field: atomar
    }
  }
  return out;
}

export interface DocumentModel {
  title: string;
  blocks: DocBlock[]; // Dokumentreihenfolge
  meta?: {
    wordCount?: number;
    truncated?: boolean;
    /** Anzahl Absätze, deren Reihenfolge-Zuordnung (Order-Zip) per Text NICHT verifiziert werden konnte. */
    unverified?: number;
  };
}
